/**
 * The face a set wears when its lane did not answer.
 *
 * A failed lane and an empty lane are different facts and they must not look
 * alike: an empty set is a finished answer, and a failed one is an answer that
 * never arrived. So this face is caution-toned, states WHICH lane failed and
 * WHAT it said, and — unlike the other two — offers the retry when the host
 * has one.
 *
 * The vendor's own error text goes in the provenance slot rather than the
 * headline. `HTTP 429 upstream rate limit` is not a sentence written for a
 * reader, and putting it where the answer should be makes the surface look
 * broken rather than honest.
 *
 * That disclosure summarises WHICH lane the handles belong to, not why the lane
 * failed. It used to summarise the reason, which printed the one sentence on
 * this face twice — once as the face's own statement and again three lines below
 * it — and a summary that repeats the line above it is exactly what
 * `CresultcardDisclosure` forbids: it describes the closed state instead of
 * answering from it.
 */
"use client";
import type { ReactElement } from "react";
import { IconPlugConnectedX } from "@tabler/icons-react";
import {
  CRC_R_CORE,
  CRC_TEXT,
  CRC_TONE_COLOR,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import {
  CresultcardActionTone,
  CresultcardTextStep,
  CresultcardTone,
} from "./cresultcard.types";
import { CresultcardAction } from "./CresultcardAction";
import { CresultcardProvenance } from "./CresultcardProvenance";

const GLYPH_SIZE = 20;
const GLYPH_STROKE = 1.4;
const RETRY_LABEL = "Try again";
/**
 * What the closed disclosure holds, which is the fact that makes leaving it
 * closed acceptable: the vendor's own words, not the answer. The reason is
 * already stated in full one line above, so restating it here left the face
 * printing its one sentence twice.
 */
const PROVENANCE_SUMMARY = "What the lane returned";

interface CresultcardFailureFaceProps {
  /** Which lane failed, in the reader's terms. */
  readonly lane: string;
  /** What it means for this surface — one sentence, no status codes. */
  readonly reason: string;
  /** The vendor's own strings, kept out of the headline. */
  readonly detail?: readonly string[];
  readonly onRetry?: () => void;
}

export function CresultcardFailureFace({
  lane,
  reason,
  detail = [],
  onRetry,
}: CresultcardFailureFaceProps): ReactElement {
  const color = CRC_TONE_COLOR[CresultcardTone.Caution];

  return (
    <div
      data-el="Cresultcard/FailureFace/Status"
      data-crc-face="failed"
      role="status"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 10,
        minWidth: 0,
        margin: 13,
        paddingBlock: 16,
        paddingInline: 14,
        borderRadius: CRC_R_CORE,
        backgroundColor: "rgba(242,159,87,0.07)",
        boxShadow: "inset 0 0 0 1px rgba(242,159,87,0.24)",
      }}
    >
      <span
        style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}
      >
        <IconPlugConnectedX
          color={color}
          size={GLYPH_SIZE}
          stroke={GLYPH_STROKE}
        />
        <span
          style={{
            fontSize: 12.5,
            fontWeight: 730,
            letterSpacing: "-0.012em",
            color: CRC_TEXT,
          }}
        >
          {lane}
        </span>
      </span>
      <span
        style={{
          ...CRC_TYPE_STYLE[CresultcardTextStep.Body],
          maxWidth: "52ch",
          color,
        }}
      >
        {reason}
      </span>
      {onRetry != null && (
        <CresultcardAction
          compact
          label={RETRY_LABEL}
          onPress={onRetry}
          tone={CresultcardActionTone.Secondary}
        />
      )}
      {detail.length > 0 && (
        <CresultcardProvenance
          primary={PROVENANCE_SUMMARY}
          secondary={detail}
        />
      )}
    </div>
  );
}
